import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import HlcFooter from "../components/HlcFooter";
import { getCutoutImage } from "../utils/getCutoutImage";
import { API_BASE_URL } from "../config/apiConfig";
import { cssVars } from "../theme";
import { FilterIcon, ChevronIcon, CloseIcon, ShieldIcon, TruckIcon } from "../components/shop/ShopIcons";
import "../styles/hlcShopSystem.css";

const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3 6 6 .5-4.5 4 1.5 6-6-3.3-6 3.3 1.5-6L3 8.5 9 8z" /></svg>
);
const BoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L4 14h7l-2 8 9-12h-7z" /></svg>
);
const GridIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
);
const HeadsetIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 13a8 8 0 0 1 16 0" /><path d="M4 13v4a2 2 0 0 0 2 2h1v-6H5a1 1 0 0 0-1 1z" /><path d="M20 13v4a2 2 0 0 1-2 2h-1v-6h1a1 1 0 0 1 2 1z" /></svg>
);
const EnergyLeafIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M11 3C7 5 5 8 5 12a7 7 0 0 0 14 0c0-1.5-.5-3-1.5-4.5C16 9 14 9 13 7c-.8-1.5-1-2.7-2-4z" /></svg>
);

const TRUST_ITEMS = [
  { icon: StarIcon, title: "Premium Quality", text: "Built to last" },
  { icon: BoltIcon, title: "Energy Efficient", text: "Save up to 85%" },
  { icon: GridIcon, title: "Wide Range", text: "For every space" },
  { icon: HeadsetIcon, title: "Trusted by Experts", text: "Industry preferred" },
];

export default function HLCCategories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [filterCategory, setFilterCategory] = useState("");
  const [filterProduct, setFilterProduct] = useState("");

  /* ================= FETCH CATEGORIES FROM adminhlc-api (MySQL) — unchanged endpoint/parsing ================= */
  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE_URL}/api/public/categories/list.php`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const parsed = Array.isArray(data)
          ? data.map((cat) => ({
              ...cat,
              subcategories: cat.subcategories
                ? Object.keys(cat.subcategories).map((sid) => cat.subcategories[sid])
                : [],
            }))
          : [];
        setCategories(parsed);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch categories:", err);
        if (!cancelled) {
          setCategories([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /* ================= NAVIGATE TO PRODUCT LISTING — unchanged real navigation ================= */
  const handleNavigate = (category, subcategory) => {
    navigate("/product-by-categories", {
      state: {
        category: category.title,
        subcategories: category.subcategories || [],
        ...(subcategory && {
          subcategory: subcategory.title,
          subcategoryDescription: subcategory.description || "",
        }),
      },
    });
  };

  /* ================= SIDEBAR FILTER — reuses the same real navigation as
     the category cards themselves; the "Product" dropdown lists that
     category's real subcategories fetched above. Not a new/invented
     filtering mechanism, just a second entry point into it. ================= */
  const selectedCategoryObj = categories.find((c) => c.title === filterCategory);
  const subcategoryOptions = selectedCategoryObj?.subcategories || [];

  const handleCategoryChange = (e) => {
    setFilterCategory(e.target.value);
    setFilterProduct("");
  };

  const applyFilters = () => {
    if (!selectedCategoryObj) return;
    const subcat = subcategoryOptions.find((s) => s.title === filterProduct);
    handleNavigate(selectedCategoryObj, subcat);
    setMobileFiltersOpen(false);
  };

  const clearFilters = () => {
    setFilterCategory("");
    setFilterProduct("");
  };

  const filterPanel = (
    <>
      <div className="hlc-shop__filters-head">
        <h3>Filters</h3>
        <button type="button" className="hlc-shop__filters-close" onClick={() => setMobileFiltersOpen(false)} aria-label="Close filters">
          <CloseIcon />
        </button>
      </div>

      <label htmlFor="cat_category">Category</label>
      <div className="hlc-shop__select-wrap">
        <select id="cat_category" value={filterCategory} onChange={handleCategoryChange} className={filterCategory ? "has-value" : ""}>
          <option value="">All Categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.title}>{c.title}</option>
          ))}
        </select>
        <ChevronIcon />
      </div>

      <label htmlFor="cat_product">Product</label>
      <div className="hlc-shop__select-wrap">
        <select id="cat_product" value={filterProduct} onChange={(e) => setFilterProduct(e.target.value)} className={filterProduct ? "has-value" : ""} disabled={!filterCategory}>
          <option value="">All Products</option>
          {subcategoryOptions.map((s) => (
            <option key={s.id} value={s.title}>{s.title}</option>
          ))}
        </select>
        <ChevronIcon />
      </div>

      <div className="filter-actions">
        <button type="button" id="applyFilters" onClick={applyFilters}>Apply Filters</button>
        <button type="button" id="clearFilters" onClick={clearFilters}>Clear All</button>
      </div>
    </>
  );

  return (
    <>
      <style>{`
        #hlc-cat-page, #hlc-cat-page * { box-sizing: border-box; }
        #hlc-cat-page {
          font-family: "Open Sans", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
          background: var(--hlc-gradient-bg);
          color: var(--hlc-text);
        }

        /* ---------- cinematic hero ---------- */
        .hlc-cat-hero {
          position: relative;
          padding: clamp(32px, 4vw, 48px) 20px clamp(20px, 2.5vw, 28px);
          text-align: center;
          overflow: hidden;
        }
        .hlc-cat-hero::before {
          content: "";
          position: absolute; inset: 0;
          background:
            radial-gradient(50% 60% at 15% 10%, rgba(155,31,108,0.20), transparent 65%),
            radial-gradient(45% 55% at 88% 15%, rgba(155,31,108,0.14), transparent 60%),
            radial-gradient(70% 50% at 50% 100%, rgba(84,39,87,0.22), transparent 65%);
          pointer-events: none;
        }
        .hlc-cat-hero__inner { position: relative; width: min(760px, 92vw); margin: 0 auto; }
        .hlc-cat-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          margin: 0 0 16px;
          font-size: 11px; font-weight: 800; letter-spacing: 0.28em; text-transform: uppercase;
          color: var(--hlc-magenta-light);
        }
        .hlc-cat-eyebrow::before, .hlc-cat-eyebrow::after { content: ""; width: 22px; height: 1px; background: rgba(224,70,143,0.5); }
        .hlc-cat-hero h1 {
          margin: 0 0 16px;
          font-size: clamp(30px, 5vw, 46px);
          font-weight: 800;
          letter-spacing: -0.01em;
          line-height: 1.18;
          color: #fff;
        }
        .hlc-cat-hero h1 span { color: var(--hlc-magenta-light); }
        .hlc-cat-hero p {
          margin: 0 auto;
          max-width: 560px;
          font-size: 14.5px;
          line-height: 1.6;
          color: var(--hlc-text-secondary);
        }

        /* ---------- trust strip ---------- */
        .hlc-cat-trust {
          position: relative;
          width: min(980px, 92vw);
          margin: clamp(24px, 3.5vw, 34px) auto 0;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0;
          background: var(--hlc-surface);
          border: 1px solid var(--hlc-border);
          border-radius: var(--hlc-radius-sm);
          padding: 16px 10px;
        }
        .hlc-cat-trust__item {
          display: flex; align-items: center; gap: 10px;
          padding: 6px 20px;
          border-right: 1px solid var(--hlc-border);
        }
        .hlc-cat-trust__item:last-child { border-right: none; }
        .hlc-cat-trust__icon {
          flex-shrink: 0; width: 30px; height: 30px; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          color: var(--hlc-magenta-light);
          background: rgba(155,31,108,0.12);
          border: 1px solid rgba(155,31,108,0.3);
        }
        .hlc-cat-trust__icon svg { width: 14px; height: 14px; }
        .hlc-cat-trust__item h4 { margin: 0; font-size: 12px; font-weight: 800; color: #fff; }
        .hlc-cat-trust__item p { margin: 0; font-size: 11px; color: var(--hlc-text-secondary); }

        /* ---------- layout ---------- */
        .hlc-cat-section { padding: clamp(20px, 2.5vw, 28px) 0 clamp(32px, 4vw, 48px); }
        .hlc-cat-inner {
          width: min(1360px, 94vw);
          margin: 0 auto;
          display: grid;
          grid-template-columns: 240px minmax(0, 1fr);
          gap: 24px;
          align-items: start;
        }

        .hlc-cat-sidebar { display: flex; flex-direction: column; gap: 16px; position: sticky; top: 90px; }
        /* .hlc-shop__filters is itself position:sticky in the shared
           stylesheet (for pages where it's the only sticky element in its
           column). Nested inside .hlc-cat-sidebar here, two independent
           sticky anchors both targeting top:90px fought each other and
           made the Need Help card underneath drift/overlap while
           scrolling — this wrapper is the single sticky unit instead. */
        .hlc-cat-sidebar .hlc-shop__filters { position: static; top: auto; }
        .hlc-cat-help {
          background: var(--hlc-surface);
          border: 1px solid var(--hlc-border);
          border-radius: var(--hlc-radius-sm);
          padding: 18px;
        }
        .hlc-cat-help__icon {
          width: 34px; height: 34px; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          color: var(--hlc-magenta-light);
          background: rgba(155,31,108,0.12);
          border: 1px solid rgba(155,31,108,0.3);
          margin-bottom: 10px;
        }
        .hlc-cat-help__icon svg { width: 16px; height: 16px; }
        .hlc-cat-help h4 { margin: 0 0 6px; font-size: 13px; font-weight: 800; color: #fff; }
        .hlc-cat-help p { margin: 0 0 14px; font-size: 12px; line-height: 1.6; color: var(--hlc-text-secondary); }
        .hlc-cat-help a {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 12px; font-weight: 700; color: var(--hlc-magenta-light);
          text-decoration: none; transition: color 0.2s ease;
        }
        .hlc-cat-help a:hover { color: #fff; }

        /* ---------- category grid + cards ---------- */
        .hlc-cat-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }

        .hlc-cat-card {
          position: relative;
          display: flex;
          flex-direction: column;
          background: linear-gradient(165deg, var(--hlc-bg-secondary) 0%, var(--hlc-bg) 100%);
          border: 1px solid var(--hlc-border);
          border-radius: var(--hlc-radius-sm);
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
        }
        .hlc-cat-card:hover {
          transform: translateY(-4px);
          border-color: rgba(155, 31, 108, 0.5);
          box-shadow: 0 20px 40px rgba(155, 31, 108, 0.22);
        }
        .hlc-cat-card__imgwrap { position: relative; aspect-ratio: 4 / 3.1; overflow: hidden; }
        .hlc-cat-card__imgwrap::before {
          content: "";
          position: absolute; inset: 8%;
          background: radial-gradient(circle at 50% 55%, rgba(155,31,108,0.20), transparent 70%);
          filter: blur(18px);
        }
        .hlc-cat-card__imgwrap img {
          position: relative; width: 100%; height: 100%; object-fit: contain; display: block;
          padding: 22px;
          transition: transform 0.4s ease;
        }
        .hlc-cat-card:hover .hlc-cat-card__imgwrap img { transform: scale(1.045); }

        .hlc-cat-card__body { padding: 18px 20px 20px; flex: 1; display: flex; flex-direction: column; }
        .hlc-cat-card__title { margin: 0 0 8px; font-size: 16px; font-weight: 800; color: #fff; letter-spacing: 0.01em; }
        .hlc-cat-card__desc { margin: 0 0 16px; font-size: 12.5px; line-height: 1.6; color: var(--hlc-text-secondary); }
        .hlc-cat-card__cta {
          margin-top: auto;
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
          color: var(--hlc-magenta-light);
          transition: color 0.2s ease;
        }
        .hlc-cat-card:hover .hlc-cat-card__cta { color: #fff; }

        /* ---------- responsive ---------- */
        @media (max-width: 1200px) {
          .hlc-cat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 991px) {
          .hlc-cat-inner { grid-template-columns: 1fr; }
          .hlc-shop__mobile-filters-btn { display: inline-flex; }
          .hlc-cat-sidebar { position: static; }
          .hlc-cat-trust__item { border-right: none; }
        }
        @media (max-width: 767px) {
          .hlc-cat-grid { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; gap: 14px !important; }
          .hlc-cat-trust { flex-direction: column; align-items: stretch; }
          .hlc-cat-trust__item { justify-content: flex-start; padding: 10px 8px; border-right: none; border-bottom: 1px solid var(--hlc-border); }
          .hlc-cat-trust__item:last-child { border-bottom: none; }
        }
        @media (max-width: 480px) {
          .hlc-cat-hero { padding-top: 36px; }
        }
      `}</style>

      <Navbar />
      <div style={{ marginTop: 45 }} />

      <div id="hlc-cat-page" className="hlc-shop-system" style={cssVars}>
        <section className="hlc-cat-hero">
          <div className="hlc-cat-hero__inner">
            <span className="hlc-cat-eyebrow">Product Families</span>
            <h1>Lighting Solutions<br /><span>by Category</span></h1>
            <p>
              HLC LEDs for every application – from bulbs, downlights, batten, panel,
              flood and decorative lighting.
            </p>
          </div>

          <div className="hlc-cat-trust">
            {TRUST_ITEMS.map(({ icon: Icon, title, text }) => (
              <div className="hlc-cat-trust__item" key={title}>
                <span className="hlc-cat-trust__icon"><Icon /></span>
                <div><h4>{title}</h4><p>{text}</p></div>
              </div>
            ))}
          </div>
        </section>

        <section className="hlc-cat-section">
          <div className="hlc-cat-inner">
            <div className={`hlc-shop__filters-scrim${mobileFiltersOpen ? " is-open" : ""}`} onClick={() => setMobileFiltersOpen(false)} />

            <div className="hlc-cat-sidebar">
              <aside className={`hlc-shop__filters${mobileFiltersOpen ? " is-open" : ""}`}>
                {filterPanel}
              </aside>

              <div className="hlc-cat-help">
                <span className="hlc-cat-help__icon"><HeadsetIcon /></span>
                <h4>Need Help?</h4>
                <p>Our lighting experts are here to help you.</p>
                <a href="/contact">Contact Us →</a>
              </div>
            </div>

            <main>
              {!loading && (
                <div className="hlc-shop__toolbar" style={{ margin: "0 0 16px" }}>
                  <button type="button" className="hlc-shop__mobile-filters-btn" onClick={() => setMobileFiltersOpen(true)}>
                    <FilterIcon /> Filters
                  </button>
                </div>
              )}

              <div className="hlc-cat-grid">
                {loading ? (
                  <div className="hlc-shop__loading">Loading categories…</div>
                ) : categories.length === 0 ? (
                  <div className="hlc-shop__empty">No categories found.</div>
                ) : (
                  categories.map((category, idx) => {
                    const image = getCutoutImage(category);
                    return (
                      <article
                        key={category.id}
                        className="hlc-cat-card"
                        onClick={() => handleNavigate(category)}
                      >
                        <div className="hlc-cat-card__imgwrap">
                          {image ? (
                            <img src={image} alt={category.title || "Category"} loading={idx < 6 ? "eager" : "lazy"} decoding="async" />
                          ) : (
                            <div className="hlc-prod-card__placeholder">No Image</div>
                          )}
                        </div>
                        <div className="hlc-cat-card__body">
                          <h3 className="hlc-cat-card__title">{category.title || "Untitled Category"}</h3>
                          <p className="hlc-cat-card__desc">
                            {category.description || "Explore premium HLC lighting solutions for this category."}
                          </p>
                          <span className="hlc-cat-card__cta">View Category →</span>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </main>
          </div>
        </section>

        <div className="hlc-shop__benefits">
          <div className="hlc-shop__benefits-inner">
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon"><ShieldIcon /></span>
              <div><h4>1-Year Warranty</h4><p>Peace of mind, always.</p></div>
            </div>
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon"><EnergyLeafIcon /></span>
              <div><h4>Energy Efficient</h4><p>Save up to 85% electricity.</p></div>
            </div>
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon"><TruckIcon /></span>
              <div><h4>Nationwide Delivery</h4><p>Fast &amp; reliable across Pakistan.</p></div>
            </div>
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon"><HeadsetIcon /></span>
              <div><h4>Dedicated Support</h4><p>We're here to help you.</p></div>
            </div>
          </div>
        </div>
      </div>

      <HlcFooter />
    </>
  );
}
